import { FaTimes, FaTrophy, FaMedal, FaAward } from "react-icons/fa";
import { useEffect, useState } from "react";

interface Score {
  id: string;
  score: number;
  level: number;
  playDate: string;
  customer: {
    username: string;
    email: string;
  };
}

interface LeaderboardProps {
  isOpen: boolean;
  onClose: () => void;
  gameName?: string;
}

export const Leaderboard = ({ isOpen, onClose, gameName = 'vivaTapsGoldRush' }: LeaderboardProps) => {
  const [scores, setScores] = useState<Score[]>([]);
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState<'monthly' | 'weekly'>('monthly');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchLeaderboard();
    }
  }, [isOpen, period]);

  const fetchLeaderboard = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000'}/public/games/leaderboard?gameName=${encodeURIComponent(gameName)}&period=${period}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to load leaderboard: ${response.status}`);
      }

      const data = await response.json();
      console.log('Leaderboard data:', data);
      
      if (data.success && data.data?.leaderboard) {
        setScores(data.data.leaderboard);
      } else {
        setScores([]);
      }
    } catch (error: any) {
      console.error('Error fetching leaderboard:', error);
      setError(error.message || 'Failed to load leaderboard');
      setScores([]);
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (index: number) => {
    switch (index) {
      case 0:
        return <FaTrophy className="text-yellow-400" size={20} />;
      case 1:
        return <FaMedal className="text-gray-400" size={20} />;
      case 2:
        return <FaAward className="text-amber-600" size={20} />;
      default:
        return <span className="text-viva-purple font-bold w-6">#{index + 1}</span>;
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch {
      return dateString;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="absolute inset-0 bg-black/80 flex items-center justify-center z-50 pointer-events-auto">
      <div className="bg-viva-surface border border-viva-border rounded-xl p-6 mx-4 w-full max-w-sm">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-viva-white flex items-center gap-2">
            <FaTrophy className="text-yellow-400" />
            Leaderboard
          </h2>
          <button 
            onClick={onClose}
            className="btn btn-ghost btn-sm text-viva-white pointer-events-auto"
          >
            <FaTimes size={20} />
          </button>
        </div>

        {/* Period Toggle */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setPeriod('weekly')}
            className={`flex-1 btn btn-sm pointer-events-auto ${
              period === 'weekly' 
                ? 'btn-primary' 
                : 'btn-outline'
            }`}
            style={period === 'weekly' ? { backgroundColor: 'hsl(var(--viva-purple))', borderColor: 'hsl(var(--viva-purple))' } : { borderColor: 'hsl(var(--viva-purple))', color: 'hsl(var(--viva-purple))' }}
          >
            This Week
          </button>
          <button
            onClick={() => setPeriod('monthly')}
            className={`flex-1 btn btn-sm pointer-events-auto ${
              period === 'monthly' 
                ? 'btn-primary' 
                : 'btn-outline'
            }`}
            style={period === 'monthly' ? { backgroundColor: 'hsl(var(--viva-purple))', borderColor: 'hsl(var(--viva-purple))' } : { borderColor: 'hsl(var(--viva-purple))', color: 'hsl(var(--viva-purple))' }}
          >
            This Month
          </button>
        </div>
        
        <div className="space-y-2 mb-4 max-h-96 overflow-y-auto">
          {loading ? (
            <div className="text-viva-gray text-center py-8">
              <div className="loading loading-spinner loading-lg mx-auto mb-2"></div>
              Loading leaderboard...
            </div>
          ) : error ? (
            <div className="text-red-400 text-center py-8">
              <p className="mb-2">❌ {error}</p>
              <button
                onClick={fetchLeaderboard}
                className="btn btn-sm btn-primary pointer-events-auto"
                style={{ backgroundColor: 'hsl(var(--viva-purple))', borderColor: 'hsl(var(--viva-purple))' }}
              >
                Retry
              </button>
            </div>
          ) : scores.length === 0 ? (
            <p className="text-viva-gray text-center py-8">
              No scores yet for this period!<br />
              <span className="text-sm">Be the first to play! 🎮</span>
            </p>
          ) : (
            scores.map((scoreEntry, index) => (
              <div 
                key={scoreEntry.id} 
                className={`flex items-center justify-between p-3 rounded transition-colors ${
                  index < 3 
                    ? 'bg-gradient-to-r from-viva-purple/30 to-viva-black/50 border border-viva-purple/50' 
                    : 'bg-viva-black/50 hover:bg-viva-black/70'
                }`}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="flex-shrink-0">
                    {getRankIcon(index)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-viva-white font-semibold truncate">
                        {scoreEntry.customer.username || 'Anonymous'}
                      </span>
                      {index < 3 && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-viva-purple/30 text-viva-purple font-semibold">
                          TOP {index + 1}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-viva-gray">
                      <span>{scoreEntry.score.toLocaleString()} pts</span>
                      <span>•</span>
                      <span>Lvl {scoreEntry.level}</span>
                      <span>•</span>
                      <span>{formatDate(scoreEntry.playDate)}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
        
        <button 
          onClick={onClose}
          className="w-full btn btn-primary pointer-events-auto"
          style={{ backgroundColor: 'hsl(var(--viva-purple))', borderColor: 'hsl(var(--viva-purple))' }}
        >
          Close
        </button>
      </div>
    </div>
  );
};
